import { useEffect, useState } from 'react';
import { useForm } from '@mantine/form';
import { Alert, Box, Button, Group, Input, Stack, Switch, Text, TextInput, Title } from '@mantine/core';
import { modals } from '@mantine/modals';
import { isAxiosError } from 'axios';
import { IconAlertCircle, IconPlus } from '@tabler/icons-react';
import { companiesApi } from '../../services/companiesApi';
import { getApiErrorMessage, getApiViolations } from '../../utils/errorHandling';
import { mapViolations } from '../../utils/mapViolations';
import { RegionLabels } from '../../types/entities';
import type { Company, CompanyFormData, Plant } from '../../types/entities';
import { CreatePlantModal } from './CreatePlantModal';
import { effectiveActiveKilns, formatIsoDateDe, isPlantActive } from './kilnActivity';
import { KilnChip, NoKilnChip } from './KilnChip';
import type { DirtyGuardApi } from './useDirtyGuard';
import classes from './CompanyDetail.module.css';

interface CompanyDetailProps {
  company: Company;
  /** Bereits auf dieses Unternehmen gefilterte Werke. */
  plants: Plant[];
  reload: () => Promise<void>;
  onJumpToPlant: (plantId: number, companyId: number) => void;
  onDeleted: () => void;
  /** Plan 28-08 Task 3: meldet den aktuellen Vergleichszustand an den Ebenenwechsel-Schutz in
   * `MasterDataPage` (UI-SPEC Punkt 5). */
  dirtyGuard: DirtyGuardApi;
}

interface PlantJumpRowProps {
  plant: Plant;
  onOpen: () => void;
}

function PlantJumpRow({ plant, onOpen }: PlantJumpRowProps) {
  // Nur die heute effektiv laufenden Oefen (D-11: Ofen UND Werk aktiv) -- die Uebersicht
  // beantwortet "womit arbeitet dieses Werk aktuell". Stillgelegte Oefen stehen im Werksdetail,
  // wo sich ihr Stilllegungsdatum auch aendern laesst.
  const closedNow = !isPlantActive(plant);
  const kilns = effectiveActiveKilns(plant);
  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={`Werk ${plant.name} öffnen`}
      className={classes.plantJumpRow}
      onClick={onOpen}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onOpen();
        }
      }}
    >
      <span className={classes.plantJumpName}>{plant.name}</span>
      <span className={classes.plantJumpRegion}>
        {plant.region ? RegionLabels[plant.region] : 'Region fehlt'}
      </span>
      {/* D-13: Drehöfen-Zelle bleibt bei einem stillgelegten Werk leer (kein "kein Drehofen"). */}
      {closedNow ? (
        <span className={classes.plantJumpKilns} />
      ) : (
        <span className={classes.plantJumpKilns}>
          {kilns.length > 0 ? kilns.map((kiln) => <KilnChip key={kiln.id} kiln={kiln} />) : <NoKilnChip />}
        </span>
      )}
      <span className={classes.plantJumpDate}>
        {plant.activeUntil ? formatIsoDateDe(plant.activeUntil) : ''}
      </span>
      <span className={classes.plantJumpArrow} aria-hidden="true">
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={2}>
          <path d="M6 3l5 5-5 5" />
        </svg>
      </span>
    </div>
  );
}

/**
 * Unternehmensdetail (D-01, D-03, D-04, D-21, D-22) -- Aufbau woertlich nach `renderCompanyDetailB`
 * aus Sketch 008 Variante B. Eigener saving-/formError-Zustand (D-02: jede Ebene speichert fuer
 * sich); der Speichern-Knopf ist deaktiviert, solange die Felder dem geladenen Stand entsprechen,
 * und aendert sein Label nur waehrend des laufenden Requests (UI-SPEC Punkt 5/Copywriting-Tabelle).
 */
export function CompanyDetail({
  company,
  plants,
  reload,
  onJumpToPlant,
  onDeleted,
  dirtyGuard,
}: CompanyDetailProps) {
  const [formError, setFormError] = useState<string | null>(null);
  // D-03: eigener Zustand fuer den 422-Loeschschutz -- getrennt von `formError` (Speicherfehler),
  // ohne Aktionsknopf (Sketch 027 `m-ok`-Handler, Unternehmenszweig).
  const [deleteError, setDeleteError] = useState<{ title: string; message: string } | null>(null);
  const [saving, setSaving] = useState(false);
  const [createPlantOpen, setCreatePlantOpen] = useState(false);

  // `controlled` (Mantine-Standard) statt des `uncontrolled`-Modus der Meldeformulare: dort geht
  // es um 280 Felder, hier um zwei -- und der Speichern-Knopf haengt an `isDirty()`, das im
  // uncontrolled-Modus zwischen den Renders nicht nachzieht.
  // Keine `validate`-Config: die Feldfehler kommen wie im uebrigen Projekt ausschliesslich vom
  // Backend (setErrors mit mapViolations im catch-Zweig).
  const form = useForm<CompanyFormData>({
    mode: 'controlled',
    initialValues: { name: company.name, csiMember: company.csiMember },
  });

  const isDirty = form.isDirty();

  // Meldet bei jedem Render an den Ebenenwechsel-Schutz (UI-SPEC Punkt 5), ob diese Ebene
  // ungespeicherte Aenderungen traegt -- beim Verlassen der Komponente (Auswahlwechsel) raeumt
  // die Cleanup-Funktion den Reststand weg.
  useEffect(() => {
    dirtyGuard.track('Unternehmen', company.name, isDirty);
    return () => dirtyGuard.clear();
  });

  function handleDiscard() {
    form.reset();
    setFormError(null);
    setDeleteError(null);
  }

  async function handleSubmit(values: CompanyFormData) {
    setFormError(null);
    setDeleteError(null);
    setSaving(true);
    try {
      const saved = await companiesApi.update(company.id, values);
      // Der gespeicherte Stand wird zum neuen Vergleichsstand -- und zwar mit den Werten aus der
      // Antwort, nicht den abgeschickten: normalisiert der Server etwas, gilt seine Fassung.
      form.setInitialValues({ name: saved.name, csiMember: saved.csiMember });
      form.setValues({ name: saved.name, csiMember: saved.csiMember });
      form.resetDirty();
      await reload();
    } catch (err) {
      form.setErrors(mapViolations(getApiViolations(err)));
      setFormError(getApiErrorMessage(err, 'Fehler beim Speichern'));
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    try {
      await companiesApi.delete(company.id);
      onDeleted();
      await reload();
    } catch (err) {
      // D-03: 422 heisst Loeschschutz (mindestens ein Werk hat eine abgegebene Meldung) -- eigener
      // Alert mit Backend-Text, kein Aktionsknopf (der sitzt nur beim Werk, dort mit Sprung ins
      // Datumsfeld). Andere Fehler (Netzwerk, 500er) bleiben ueber `formError` wie bisher.
      if (isAxiosError(err) && err.response?.status === 422) {
        setDeleteError({
          title: `${company.name} lässt sich nicht löschen.`,
          message: getApiErrorMessage(err, 'Das Unternehmen lässt sich nicht löschen.'),
        });
      } else {
        setFormError(getApiErrorMessage(err, 'Fehler beim Löschen'));
      }
    }
  }

  function handleDeleteClick() {
    modals.openConfirmModal({
      title: 'Unternehmen löschen',
      children: (
        <>
          <Text size="sm">
            {company.name} wird mit allen Werken, Drehöfen und offenen Meldungen endgültig gelöscht.
          </Text>
          <Text size="sm" c="vdzGray.6" mt="xs">
            Hat eines der Werke schon eingereichte oder freigegebene Meldungen, lässt sich das
            Unternehmen nicht löschen – legen Sie die Werke dann still.
          </Text>
        </>
      ),
      labels: { confirm: 'Löschen', cancel: 'Abbrechen' },
      confirmProps: { color: 'red' },
      onConfirm: handleDelete,
    });
  }

  async function handlePlantCreated(plantId: number) {
    await reload();
    onJumpToPlant(plantId, company.id);
  }

  return (
    <Box component="form" onSubmit={form.onSubmit(handleSubmit)}>
      <Text size="sm" c="vdzGray.6" mb={2}>
        Unternehmen
      </Text>
      <Title order={2} tabIndex={-1}>
        {company.name}
      </Title>

      {deleteError && (
        <Alert icon={<IconAlertCircle size={16} />} color="red" title={deleteError.title} mt="md">
          {deleteError.message}
        </Alert>
      )}
      {formError && (
        <Alert icon={<IconAlertCircle size={16} />} color="red" mt="md">
          {formError}
        </Alert>
      )}

      <Box
        mt="md"
        style={{ display: 'grid', gridTemplateColumns: '1fr 240px', gap: 14, maxWidth: 560 }}
      >
        <TextInput label="Name" required {...form.getInputProps('name')} />
        {/* Input.Wrapper statt eines eigenen Text-Labels: uebernimmt die Label-Typografie aus dem
            Theme (700/14px), damit "CSI-Mitglied" genauso wirkt wie "Name" daneben. Der Schalter
            sitzt in einem Kasten mit der Hoehe eines sm-Eingabefelds und ist darin vertikal
            zentriert -- so liegt er auf einer Linie mit dem Namensfeld statt am Label zu kleben. */}
        <Input.Wrapper label="CSI-Mitglied" error={form.errors.csiMember}>
          <Box className={classes.switchField}>
            <Switch label="Ja" {...form.getInputProps('csiMember', { type: 'checkbox' })} />
          </Box>
        </Input.Wrapper>
      </Box>

      <Box mt={26}>
        <Group justify="space-between" align="center" mb="sm" wrap="nowrap">
          <Text fw={700} size="sm">
            Werke ({plants.length})
          </Text>
          <Button
            variant="default"
            size="xs"
            leftSection={<IconPlus size={14} />}
            onClick={() => setCreatePlantOpen(true)}
          >
            Werk hinzufügen
          </Button>
        </Group>
        {plants.length === 0 ? (
          <Text size="sm" c="vdzGray.6">
            Für dieses Unternehmen sind noch keine Werke hinterlegt.
          </Text>
        ) : (
          <Stack gap={0} className={classes.plantJumpList}>
            <Box className={classes.plantJumpHeader}>
              <span>Werk</span>
              <span>Region</span>
              <span>Drehöfen</span>
              <span>Stillgelegt zum</span>
              <span />
            </Box>
            {plants.map((plant) => (
              <PlantJumpRow key={plant.id} plant={plant} onOpen={() => onJumpToPlant(plant.id, company.id)} />
            ))}
          </Stack>
        )}
      </Box>

      {/* Kein eigener Trennstrich: die Tabelle darueber schliesst bereits mit einer Kante ab,
          zwei Linien im Abstand einer Zeilenhoehe lasen sich wie ein leerer Tabelleneintrag. */}
      <Group justify="space-between" mt="xl" pt="md">
        <Button variant="outline" color="red" onClick={handleDeleteClick}>
          Unternehmen löschen
        </Button>
        <Group>
          <Button variant="default" onClick={handleDiscard}>
            Verwerfen
          </Button>
          <Button type="submit" disabled={!isDirty || saving} loading={saving}>
            {saving ? 'Speichert…' : 'Unternehmen speichern'}
          </Button>
        </Group>
      </Group>

      <CreatePlantModal
        opened={createPlantOpen}
        onClose={() => setCreatePlantOpen(false)}
        companyId={company.id}
        onCreated={handlePlantCreated}
      />
    </Box>
  );
}
